<script lang="ts">
	import type { CalendarEvent } from '@/types/calendar';
	import { isJobEvent, isSiteVisitEvent, isLeadEvent } from '@/types/calendar';
	import { formatDate } from './calendar.utils';
	import { MapPin, Calendar, User, Car, Briefcase, Building, ExternalLink, DollarSign } from 'lucide-svelte';
	import { Button } from '@/components/ui/button';
	import PicklistBadge from '@/components/ui/picklist-badge/PicklistBadge.svelte';
	import { router } from '@inertiajs/svelte';

	type Props = {
		event: CalendarEvent;
	};

	let { event }: Props = $props();

	function viewJobDetail() {
		router.visit(route('jobs.show', { job: event.id }));
	}

	function viewLeadDetail() {
		// Strip the '-extra-hire' suffix if present
		const leadId = event.id.replace('-extra-hire', '');
		router.visit(route('leads.show', { lead: leadId }));
	}

	function viewSiteVisitParent() {
		if (!isSiteVisitEvent(event) || !event.meta) return;

		const meta = event.meta;
		const hash = '#section-site-visits';

		if (meta.visitableType === 'Job') {
			router.visit(route('jobs.show', { job: meta.visitableId }) + hash);
		} else if (meta.visitableType === 'Lead') {
			router.visit(route('leads.show', { lead: meta.visitableId }) + hash);
		}
	}
</script>

<div class="space-y-3 p-1">
	<div class="font-semibold text-base">{event.title}</div>

	{#if isJobEvent(event) && event.meta}
		{@const meta = event.meta}
		<div class="space-y-2 text-sm">
			{#if meta.statusLabel && meta.statusColor}
				<div class="flex items-center gap-2">
					<PicklistBadge label={meta.statusLabel} color={meta.statusColor} />
				</div>
			{/if}

			{#if meta.jobTypeLabel}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Briefcase class="h-4 w-4" />
					<span>{meta.jobTypeLabel}</span>
				</div>
			{/if}

			{#if event.start}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Calendar class="h-4 w-4" />
					<span>
						{formatDate(event.start.split('T')[0])}
						{#if event.end && event.end !== event.start}
							- {formatDate(event.end.split('T')[0])}
						{/if}
					</span>
				</div>
			{/if}

			{#if meta.customerName}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Building class="h-4 w-4" />
					<span>{meta.customerName}</span>
				</div>
			{/if}

			{#if meta.address}
				<div class="flex items-center gap-2 text-muted-foreground">
					<MapPin class="h-4 w-4" />
					<span class="line-clamp-2">{meta.address}</span>
				</div>
			{/if}

			{#if meta.assignedVehicles && meta.assignedVehicles.length > 0}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Car class="h-4 w-4" />
					<span>{meta.assignedVehicles.map((v) => v.registration).join(', ')}</span>
				</div>
			{/if}

			<div class="pt-2 border-t mt-2">
				<Button variant="outline" size="sm" class="w-full gap-2" onclick={viewJobDetail}>
					<ExternalLink class="h-4 w-4" />
					View Job
				</Button>
			</div>
		</div>
	{:else if isSiteVisitEvent(event) && event.meta}
		{@const meta = event.meta}
		<div class="space-y-2 text-sm">
			<!-- Status and Purpose badges -->
			{#if (meta.statusLabel && meta.statusColor) || (meta.purposeLabel && meta.purposeColor)}
				<div class="flex flex-wrap items-center gap-2">
					{#if meta.statusLabel && meta.statusColor}
						<PicklistBadge label={meta.statusLabel} color={meta.statusColor} />
					{/if}
					{#if meta.purposeLabel && meta.purposeColor}
						<PicklistBadge label={meta.purposeLabel} color={meta.purposeColor} />
					{/if}
				</div>
			{/if}

			{#if event.start}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Calendar class="h-4 w-4" />
					<span>
						{formatDate(event.start.split('T')[0])}
						{#if event.end && event.end !== event.start}
							- {formatDate(event.end.split('T')[0])}
						{/if}
					</span>
				</div>
			{/if}

			{#if meta.visitableName}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Building class="h-4 w-4" />
					<span>{meta.visitableName}</span>
				</div>
			{/if}

			{#if meta.address}
				<div class="flex items-center gap-2 text-muted-foreground">
					<MapPin class="h-4 w-4" />
					<span class="line-clamp-2">{meta.address}</span>
				</div>
			{/if}

			{#if meta.vehicleName || meta.vehicleRegistration}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Car class="h-4 w-4" />
					<span>{meta.vehicleRegistration || meta.vehicleName}</span>
				</div>
			{/if}

			{#if meta.assignedEmployees && meta.assignedEmployees.length > 0}
				<div class="flex items-center gap-2 text-muted-foreground">
					<User class="h-4 w-4" />
					<span>{meta.assignedEmployees.map((e) => e.name).join(', ')}</span>
				</div>
			{/if}

			{#if meta.notes}
				<div class="text-muted-foreground mt-2 pt-2 border-t">
					<p class="line-clamp-3">{meta.notes}</p>
				</div>
			{/if}

			<div class="pt-2 border-t mt-2">
				<Button variant="outline" size="sm" class="w-full gap-2" onclick={viewSiteVisitParent}>
					<ExternalLink class="h-4 w-4" />
					View {meta.visitableType}
				</Button>
			</div>
		</div>
	{:else if isLeadEvent(event) && event.meta}
		{@const meta = event.meta}
		<div class="space-y-2 text-sm">
			{#if meta.statusLabel && meta.statusColor}
				<div class="flex items-center gap-2">
					<PicklistBadge label={meta.statusLabel} color={meta.statusColor} />
				</div>
			{/if}

			{#if event.start}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Calendar class="h-4 w-4" />
					<span>
						{formatDate(event.start.split('T')[0])}
						{#if event.end && event.end !== event.start}
							- {formatDate(event.end.split('T')[0])}
						{/if}
					</span>
				</div>
			{/if}

			{#if meta.customerName}
				<div class="flex items-center gap-2 text-muted-foreground">
					<Building class="h-4 w-4" />
					<span>{meta.customerName}</span>
				</div>
			{/if}

			{#if meta.address}
				<div class="flex items-center gap-2 text-muted-foreground">
					<MapPin class="h-4 w-4" />
					<span class="line-clamp-2">{meta.address}</span>
				</div>
			{/if}

			{#if meta.estimatedValue}
				<div class="flex items-center gap-2 text-muted-foreground">
					<DollarSign class="h-4 w-4" />
					<span>{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(meta.estimatedValue)}</span>
				</div>
			{/if}

			<div class="pt-2 border-t mt-2">
				<Button variant="outline" size="sm" class="w-full gap-2" onclick={viewLeadDetail}>
					<ExternalLink class="h-4 w-4" />
					View Lead
				</Button>
			</div>
		</div>
	{/if}
</div>